import { Injectable } from '@angular/core';
import {
  ConsentForm,
  ConsentFormApi,
  NppActionNeededSummary,
  NppOutcome,
  NppPendingPrompt,
  Page,
  PendingConsentSummary,
} from '@models';
import { ConsentFormsService } from '@services';
import { Observable } from 'rxjs';
import { HttpService, QueryParameters } from '../http.service';

@Injectable({
  providedIn: 'root',
})
export class ConsentFormsApiService
  extends HttpService<ConsentForm>
  implements ConsentFormsService
{
  protected override readonly collectionPath = '/consent-forms/submissions';

  /**
   * GET /smart-forms/submissions/
   */
  getConsentRequests(params: QueryParameters): Observable<Page<ConsentForm>> {
    return this.getCollectionByQuery(params);
  }

  /**
   * GET /smart-forms/submissions/{id}
   */
  getConsentForm(id: string): Observable<ConsentFormApi> {
    return this.http.get<ConsentFormApi>(`${this.apiUrl}/${id}`);
  }

  /**
   * POST /smart-forms/submissions/
   */
  assignToPatient(body: {
    patientId: string;
    templateId: string;
  }): Observable<ConsentForm> {
    return this.http.post<ConsentForm>(this.apiUrl, body);
  }

  /**
   * PATCH /smart-forms/submissions/{submissionId}/sign
   */
  sign(
    submissionId: string,
    body: {
      patientName: string;
      dateOfBirth: string;
      signature: string;
      guardianName?: string | null;
      guardianRelationship?: string | null;
    }
  ): Observable<string> {
    return this.http.patch<string>(`${this.apiUrl}/${submissionId}/sign`, body);
  }

  /**
   * PATCH /consent-forms/submissions/{submissionId}/npp-outcome
   */
  recordNppOutcome(
    submissionId: string,
    body: {
      outcome: NppOutcome;
      patientName: string;
      dateOfBirth?: string | null;
    }
  ): Observable<NppOutcome> {
    return this.http.patch<NppOutcome>(
      `${this.apiUrl}/${submissionId}/npp-outcome`,
      body
    );
  }

  /**
   * DELETE /smart-forms/submissions/{submissionId}
   */
  deleteSubmission(submissionId: string): Observable<unknown> {
    return this.deleteItemById(submissionId);
  }

  /**
   * GET /smart-forms/submissions/booking-intent/{bookingIntentId}
   */
  getSubmissionsByBookingIntentId(id: string): Observable<ConsentForm[]> {
    return this.http.get<ConsentForm[]>(`${this.apiUrl}/booking-intent/${id}`);
  }

  /**
   * /consent-form/submissions/{submissionId}/pdf
   */
  getPdf(
    submissionId: string,
    action: 'Download' | 'View' = 'Download'
  ): Observable<Blob> {
    return this.http.get(`${this.apiUrl}/${submissionId}/pdf`, {
      responseType: 'blob',
      params: this.getHttpParameters({ action }),
    });
  }

  markAsOpened(submissionId: string): Observable<void> {
    return this.http.patch<void>(`${this.apiUrl}/${submissionId}/opened`, null);
  }

  /**
   * GET
/consent-forms/submissions/appointments/{appointmentId
   */
  getSubmissionByAppointmentId(
    appointmentId: string
  ): Observable<ConsentForm[]> {
    return this.http.get<ConsentForm[]>(
      `${this.apiUrl}/appointments/${appointmentId}`
    );
  }

  /**
   * GET /consent-forms/submissions/patients/{patientId}/pending-summary
   */
  getPendingSummary(patientId: string): Observable<PendingConsentSummary> {
    return this.http.get<PendingConsentSummary>(
      `${this.apiUrl}/patients/${patientId}/pending-summary`
    );
  }

  /**
   * POST /consent-forms/submissions/{submissionId}/reminder
   */
  sendReminder(submissionId: string): Observable<void> {
    return this.http.post<void>(
      `${this.apiUrl}/${submissionId}/reminder`,
      null
    );
  }

  /**
   * GET /consent-forms/submissions/patients/{patientId}/npp-action-needed
   */
  getNppActionNeeded(patientId: string): Observable<NppActionNeededSummary> {
    return this.http.get<NppActionNeededSummary>(
      `${this.apiUrl}/patients/${patientId}/npp-action-needed`
    );
  }

  /**
   * GET /consent-forms/submissions/npp-pending
   */
  getMyPendingNpp(): Observable<NppPendingPrompt> {
    return this.http.get<NppPendingPrompt>(`${this.apiUrl}/npp-pending`);
  }

  /**
   * PATCH /consent-forms/submissions/{submissionId}/npp-portal-acknowledge
   */
  acknowledgeNppFromPortal(
    submissionId: string,
    body: {
      patientName: string;
      dateOfBirth?: string | null;
    }
  ): Observable<NppOutcome> {
    return this.http.patch<NppOutcome>(
      `${this.apiUrl}/${submissionId}/npp-portal-acknowledge`,
      body
    );
  }
}
